import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  Bell, Calendar, Check, X, Star, CreditCard, Package, Truck,
  Store, MessageCircle, Gift, Share2, Sparkles, Link as LinkIcon,
  ChevronRight, CheckCheck,
} from 'lucide-react';

const ICON_MAP = {
  calendar: Calendar,
  check: Check,
  x: X,
  star: Star,
  'credit-card': CreditCard,
  package: Package,
  truck: Truck,
  store: Store,
  'message-circle': MessageCircle,
  gift: Gift,
  share: Share2,
  sparkles: Sparkles,
  link: LinkIcon,
  bell: Bell,
};

const TYPE_TO_ICON = {
  booking_reminder: 'calendar',
  booking_confirmed: 'check',
  booking_cancelled: 'x',
  booking_completed: 'star',
  booking_paid: 'credit-card',
  order_placed: 'package',
  order_shipped: 'truck',
  order_delivered: 'check',
  order_ready: 'store',
  message: 'message-circle',
  rating: 'star',
  system: 'bell',
  cart_gifted: 'gift',
  cart_shared: 'share',
  maintenance_paid: 'sparkles',
  maintenance_pay_request: 'link',
  checkout_gifted: 'gift',
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const ms = ts.toMillis?.() ?? new Date(ts).getTime();
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
};

const ProfessionalNotificationsPage = () => {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const { unread, read } = useMemo(() => {
    const u = [];
    const r = [];
    notifications.forEach(n => {
      if (n.read) r.push(n);
      else u.push(n);
    });
    return { unread: u, read: r };
  }, [notifications]);

  const handleTap = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    deleteNotification(id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* HEADER — matches the purple header card on Products page */}
      <div className="bg-[#F4EEFF] rounded-3xl p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="text-[10px] font-bold tracking-[0.2em] text-[#AF8BD4] uppercase">
              Your Updates
            </span>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-2">
              Notifications
            </h1>
            <p className="text-gray-500 mt-1.5 text-sm">
              {notifications.length > 0
                ? unreadCount > 0
                  ? `${unreadCount} new · ${notifications.length} total`
                  : `${notifications.length} notification${notifications.length === 1 ? '' : 's'}`
                : 'Bookings, messages and payments from your clients'}
            </p>
          </div>

          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-5 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors flex items-center gap-2"
            >
              <CheckCheck size={16} />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* EMPTY STATE */}
      {notifications.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50">
          <div className="w-24 h-24 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-5">
            <Bell size={36} className="text-[#AF8BD4]" />
          </div>
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
            No notifications yet
          </h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            You'll be notified when clients book, message, or pay for your services.
          </p>
          <button
            onClick={() => navigate('/professional-dashboard')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors inline-flex items-center gap-2"
          >
            Back to Dashboard
          </button>
        </div>
      ) : (
        <>
          {/* UNREAD — "New" section */}
          {unread.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#AF8BD4]"></span>
                <h3 className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-[0.2em]">
                  New · {unread.length}
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {unread.map((notif) => (
                  <NotificationCard
                    key={notif.id}
                    notif={notif}
                    onClick={() => handleTap(notif)}
                    onDelete={handleDelete}
                    isUnread
                  />
                ))}
              </div>
            </div>
          )}

          {/* READ — "Earlier" section */}
          {read.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                  Earlier · {read.length}
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {read.map((notif) => (
                  <NotificationCard
                    key={notif.id}
                    notif={notif}
                    onClick={() => handleTap(notif)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// NOTIFICATION CARD — styled like a product card
// ============================================================
const NotificationCard = ({ notif, onClick, onDelete, isUnread }) => {
  const iconKey = TYPE_TO_ICON[notif.type] || 'bell';
  const Icon = ICON_MAP[iconKey] || Bell;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white p-3 rounded-3xl border transition-colors group flex items-start gap-3 ${
        isUnread
          ? 'border-[#CDBCE7] hover:border-[#AF8BD4]'
          : 'border-gray-100 hover:border-[#CDBCE7]'
      }`}
    >
      {/* Icon tile — purple */}
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
          isUnread ? 'bg-[#AF8BD4] text-white' : 'bg-[#F4EEFF] text-[#AF8BD4]'
        }`}
      >
        <Icon size={22} />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 py-0.5">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {isUnread && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F4EEFF] text-[#AF8BD4]">
              <span className="w-1 h-1 rounded-full bg-[#AF8BD4]"></span>
              New
            </span>
          )}
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            {timeAgo(notif.createdAt)}
          </span>
        </div>

        <h3 className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
          {notif.title}
        </h3>

        {notif.body && (
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {notif.body}
          </p>
        )}
      </div>

      {/* Right side — delete + chevron */}
      <div className="flex items-center gap-1 shrink-0 self-center">
        <button
          onClick={(e) => onDelete(e, notif.id)}
          className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors"
          title="Delete"
        >
          <X size={14} />
        </button>
        <ChevronRight size={16} className="text-gray-300 group-hover:text-[#AF8BD4] transition-colors" />
      </div>
    </button>
  );
};

export default ProfessionalNotificationsPage;